import { useMemo } from "react";
import { DATE_PRESETS, appTimeZoneLabel, todayInAppTimeZone } from "../utils/dateTime";

/**
 * From / To date filter with quick ranges (Today, Yesterday, Last 7 days ...).
 * All dates are calendar days in the business time zone (see utils/dateTime.js),
 * sent to the backend as "YYYY-MM-DD" in the startDate / endDate query params.
 *
 * Usage:
 *   const [dates, setDates] = useState({ startDate: "", endDate: "" });
 *   <DateRangeFilter value={dates} onChange={setDates} />
 *   // then add dates.startDate / dates.endDate to your API request
 */
export default function DateRangeFilter({ value, onChange, label = "Date" }) {
    const startDate = value?.startDate || "";
    const endDate = value?.endDate || "";
    const today = todayInAppTimeZone();
    const zoneLabel = useMemo(() => appTimeZoneLabel(), []);

    const activePreset = DATE_PRESETS.find((p) => {
        const r = p.range(today);
        return r.startDate === startDate && r.endDate === endDate;
    })?.key;

    const setStart = (next) => {
        // Keep the range valid: moving "From" past "To" moves "To" along.
        onChange({ startDate: next, endDate: endDate && next && next > endDate ? next : endDate });
    };
    const setEnd = (next) => {
        onChange({ startDate: startDate && next && next < startDate ? next : startDate, endDate: next });
    };

    return (
        <div style={styles.wrap}>
            <div style={styles.row}>
                <label style={styles.field}>
                    <span style={styles.caption}>{label} from</span>
                    <input
                        type="date"
                        value={startDate}
                        max={endDate || today}
                        onChange={(e) => setStart(e.target.value)}
                        style={styles.input}
                    />
                </label>
                <label style={styles.field}>
                    <span style={styles.caption}>{label} to</span>
                    <input
                        type="date"
                        value={endDate}
                        min={startDate || undefined}
                        max={today}
                        onChange={(e) => setEnd(e.target.value)}
                        style={styles.input}
                    />
                </label>
                {(startDate || endDate) && (
                    <button type="button" onClick={() => onChange({ startDate: "", endDate: "" })} style={styles.clear}>
                        Clear
                    </button>
                )}
            </div>

            <div style={styles.row}>
                {DATE_PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => onChange(p.range(today))}
                        style={activePreset === p.key ? { ...styles.preset, ...styles.presetActive } : styles.preset}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <span style={styles.hint}>Dates use {zoneLabel} time.</span>
        </div>
    );
}

const styles = {
    wrap: { display: "flex", flexDirection: "column", gap: 8 },
    row: { display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8 },
    field: { display: "flex", flexDirection: "column", gap: 4 },
    caption: { fontSize: 12, color: "#4b5563", fontWeight: 600 },
    input: { padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 },
    clear: { padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 14 },
    preset: { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 999, background: "#fff", cursor: "pointer", fontSize: 13 },
    presetActive: { background: "#16324f", borderColor: "#16324f", color: "#fff" },
    hint: { fontSize: 12, color: "#6b7280" },
};
